import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Stack,
  LinearProgress,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp,
  TrendingDown,
  Api,
  AccountBalanceWallet,
  Assessment,
  CheckCircle,
} from '@mui/icons-material';

const StyledCard = styled(Card)(() => ({
  borderRadius: '16px',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  border: '1px solid #f1f5f9',
  transition: 'all 0.2s ease-in-out',
  height: '100%',
  '&:hover': {
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-2px)',
  },
}));

const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return num.toString();
};


const MetricCard = ({ title, value, change, changeType, icon, color, subtitle }) => {
  const isPositive = changeType === 'positive';
  
  return (
    <StyledCard>
      <CardContent sx={{ p: 3, height: '160px', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748b',
                fontSize: '14px',
                fontWeight: 500,
                mb: 1
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                color: '#1e293b',
                mb: 0.5,
                fontSize: '26px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '12px'
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}15`,
              color: color,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        </Stack>
        
        {change && (
          <Stack direction="row" alignItems="center" sx={{ mt: 'auto', mb: 0 }}>
            {isPositive ? (
              <TrendingUp sx={{ fontSize: 16, color: '#10b981', mr: 0.5 }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: '#ef4444', mr: 0.5 }} />
            )}
            <Typography 
              variant="caption" 
              sx={{ 
                color: isPositive ? '#10b981' : '#ef4444',
                fontWeight: 600,
                fontSize: '12px'
              }}
            >
              {change}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#64748b',
                ml: 0.5,
                fontSize: '12px'
              }}
            >
              vs last month
            </Typography>
          </Stack>
        )}
      </CardContent>
    </StyledCard>
  );
};

const UsageCard = ({ title, current, total, percentage, color }) => {
  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: '#1e293b',
            mb: 2,
            fontSize: '16px'
          }}
        >
          {title}
        </Typography>
        
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              color: '#1e293b',
              fontSize: '24px'
            }}
          >
            {current.toLocaleString()}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#64748b',
              fontSize: '14px'
            }}
          >
            of {total.toLocaleString()}
          </Typography>
        </Stack>
        
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: '#f1f5f9',
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
              borderRadius: 4,
            },
          }}
        />
        
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#64748b',
              fontSize: '12px'
            }}
          >
            {percentage.toFixed(1)}% useda
          </Typography>
          <Chip
            label={`${(total - current).toLocaleString()} remaining`}
            size="small"
            sx={{
              backgroundColor: `${color}15`,
              color: color,
              fontSize: '11px',
              height: 24,
            }}
          />
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export const DashboardCards = ({ 
  totalApis = 0,
  totalCredits = 0,
  usedCredits = 0,
  remainingCredits = 0,
  testingCredits = 1200,
  liveCredits = 2300,
  usagePercentage = 0,
  dashboardSummaryData
}) => {
  const totalApiRequests = dashboardSummaryData?.totalApiRequests || {};
  const successRate = dashboardSummaryData?.successRate || {};
  
  const currentRequests = totalApiRequests.current || 0;
  const requestsChangePercent = totalApiRequests.changePercent || 0;
  const currentSuccessRate = successRate.current || 0;
  const successRateChangePercent = successRate.changePercent || 0;

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
          <MetricCard
            title="Total API Requests"
            value={currentRequests.toLocaleString()}
            change={`${requestsChangePercent > 0 ? "+" : ""}${requestsChangePercent}%`}
            changeType={requestsChangePercent >= 0 ? "positive" : "negative"}
            icon={<Api />}
            color="#6366f1"
            subtitle="This month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
          <MetricCard
            title="Success Rate"
            value={`${currentSuccessRate}%`}
            change={`${successRateChangePercent > 0 ? "+" : ""}${successRateChangePercent}%`}
            changeType={successRateChangePercent >= 0 ? "positive" : "negative"}
            icon={<CheckCircle />}
            color="#10b981"
            subtitle="Average uptime"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
          <MetricCard
            title="Active APIs"
            value={`${totalApis}`}
            icon={<Assessment />}
            color="#f59e0b"
            subtitle="Currently running"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
          <MetricCard
            title="Credit Balance"
            value={`${formatNumber(remainingCredits)} / ${formatNumber(totalCredits)}`}
            change={`${usagePercentage > 0 ? "+" : ""}${usagePercentage}%`}
            changeType={usagePercentage >= 0 ? "positive" : "negative"}
            icon={<AccountBalanceWallet />}
            color="#8b5cf6"
            subtitle="Credits remaining"
          />
        </Grid>
      </Grid>


    </Box>
  );
};